import { Provider, InjectionToken } from '@angular/core';
import { INCLUDE_OWNER } from '@zopup/zop-shared';
import { ApiConfigService } from './api-config.service';
import {
    AUDIT_API_BASE_PATH,
    USERS_API_BASE_PATH,
    COMPANIES_API_BASE_PATH,
    SALES_API_BASE_PATH,
    LOCATIONS_API_BASE_PATH,
    BRANDS_API_BASE_PATH,
    VENDORS_API_BASE_PATH,
    CURRENCIES_API_BASE_PATH,
    CUSTOMERS_API_BASE_PATH,
    DOCUMENTS_API_BASE_PATH,
    FINANCE_API_BASE_PATH,
    INFRASTRUCTURE_API_BASE_PATH,
    INVENTORY_API_BASE_PATH,
    SIGNALR_API_BASE_PATH,
    NOTIFICATIONS_API_BASE_PATH,
    PRODUCT_CATALOGUE_API_BASE_PATH,
    SCHEDULER_API_BASE_PATH,
    SETTINGS_API_BASE_PATH,
    SUBSCRIPTIONS_API_BASE_PATH,
    TASKS_API_BASE_PATH,
    TAX_API_BASE_PATH,
    CONTACTS_API_BASE_PATH,
    REPORTING_API_BASE_PATH,
    DEVICES_API_BASE_PATH,
    MANUFACTURING_API_BASE_PATH,
    VERIFIED_API_BASE_PATH,
} from './api-tokens';
import { S } from '@angular/cdk/keycodes';

/**
 * Environment configuration interface
 */
export interface ApiEnvironmentConfig {
    api: {
        baseUrl: string;
        endpoints: { [domain: string]: string };
    };
    environmentName?: string;
    includeOwner?: boolean;
}

/**
 * Injection token for API environment configuration
 */
export const API_ENVIRONMENT_TOKEN = new InjectionToken<ApiEnvironmentConfig>(
    'API_ENVIRONMENT_TOKEN'
);

/**
 * Provide API configuration based on environment
 */
export function provideZopApis(environment: ApiEnvironmentConfig): Provider[] {
    return [
        // Provide the environment configuration
        {
            provide: API_ENVIRONMENT_TOKEN,
            useValue: environment,
        },
        // Provide the include owner flag - used in Brands/Manufacturers APIs
        {
            provide: INCLUDE_OWNER,
            useValue: environment.includeOwner ?? false,
        },
        // Direct base path providers without relying on ApiConfigService instance
        {
            provide: AUDIT_API_BASE_PATH,
            useValue:
                environment.api.endpoints['audit'] || environment.api.baseUrl,
        },
        {
            provide: USERS_API_BASE_PATH,
            useValue:
                environment.api.endpoints['users'] || environment.api.baseUrl,
        },
        {
            provide: COMPANIES_API_BASE_PATH,
            useValue:
                environment.api.endpoints['companies'] ||
                environment.api.baseUrl,
        },
        {
            provide: SALES_API_BASE_PATH,
            useValue:
                environment.api.endpoints['sales'] || environment.api.baseUrl,
        },
        {
            provide: LOCATIONS_API_BASE_PATH,
            useValue:
                environment.api.endpoints['locations'] ||
                environment.api.baseUrl,
        },
        {
            provide: BRANDS_API_BASE_PATH,
            useValue:
                environment.api.endpoints['brands'] || environment.api.baseUrl,
        },
        {
            provide: VENDORS_API_BASE_PATH,
            useValue:
                environment.api.endpoints['vendors'] || environment.api.baseUrl,
        },
        {
            provide: CURRENCIES_API_BASE_PATH,
            useValue:
                environment.api.endpoints['currencies'] ||
                environment.api.baseUrl,
        },
        {
            provide: CUSTOMERS_API_BASE_PATH,
            useValue:
                environment.api.endpoints['customers'] ||
                environment.api.baseUrl,
        },
        {
            provide: DEVICES_API_BASE_PATH,
            useValue:
                environment.api.endpoints['devices'] ||
                environment.api.baseUrl,
        },
        {
            provide: DOCUMENTS_API_BASE_PATH,
            useValue:
                environment.api.endpoints['documents'] ||
                environment.api.baseUrl,
        },
        {
            provide: FINANCE_API_BASE_PATH,
            useValue:
                environment.api.endpoints['finance'] || environment.api.baseUrl,
        },
        {
            provide: INFRASTRUCTURE_API_BASE_PATH,
            useValue:
                environment.api.endpoints['infrastructure'] ||
                environment.api.baseUrl,
        },
        {
            provide: INVENTORY_API_BASE_PATH,
            useValue:
                environment.api.endpoints['inventory'] ||
                environment.api.baseUrl,
        },
        {
            provide: NOTIFICATIONS_API_BASE_PATH,
            useValue:
                environment.api.endpoints['notifications'] ||
                environment.api.baseUrl,
        },
        {
            provide: SIGNALR_API_BASE_PATH,
            useValue:
                environment.api.endpoints['signalr'] || environment.api.baseUrl,
        },
        {
            provide: PRODUCT_CATALOGUE_API_BASE_PATH,
            useValue:
                environment.api.endpoints['productCatalogue'] ||
                environment.api.baseUrl,
        },
        {
            provide: REPORTING_API_BASE_PATH,
            useValue:
                environment.api.endpoints['reporting'] ||
                environment.api.baseUrl,
        },
        {
            provide: SCHEDULER_API_BASE_PATH,
            useValue:
                environment.api.endpoints['scheduler'] ||
                environment.api.baseUrl,
        },
        {
            provide: SETTINGS_API_BASE_PATH,
            useValue:
                environment.api.endpoints['settings'] ||
                environment.api.baseUrl,
        },
        {
            provide: SUBSCRIPTIONS_API_BASE_PATH,
            useValue:
                environment.api.endpoints['subscriptions'] ||
                environment.api.baseUrl,
        },
        {
            provide: TASKS_API_BASE_PATH,
            useValue:
                environment.api.endpoints['tasks'] || environment.api.baseUrl,
        },
        {
            provide: TAX_API_BASE_PATH,
            useValue:
                environment.api.endpoints['tax'] || environment.api.baseUrl,
        },
        {
            provide: CONTACTS_API_BASE_PATH,
            useValue:
                environment.api.endpoints['contacts'] ||
                environment.api.baseUrl,
        },
         {
            provide: MANUFACTURING_API_BASE_PATH,
            useValue:
                environment.api.endpoints['manufacturing'] ||
                environment.api.baseUrl,
        },
        {
            provide: VERIFIED_API_BASE_PATH,
            useValue:
                environment.api.endpoints['verified'] || environment.api.baseUrl,
        },
    ];
}
